import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RootState } from "@src/app/store";

export interface SeedFileDto {
  tableName: string;
  displayName?: string;
  description?: string;
  filename: string;
  createSql: string;
  insertSql: string;
  fullContent: string;
  subjectId?: string;
  parentId?: string;
  organizationId?: string;
  scope?: string;
  createdBy?: string;
  lastModified: number;
}

export interface CustomSeedRequest {
  tableName: string;
  displayName?: string;
  description?: string;
  createSql: string;
  insertSql: string;
  subjectId?: string;
  parentId?: string;
  organizationId?: string;
  scope?: string;
  createdBy?: string;
}

export interface InteractiveSqlExecuteResponse {
  success: boolean;
  queryType?: string;
  message?: string;
  errorDetails?: string;
  columns?: string[];
  rows?: Record<string, string | number | null>[];
  affectedRows?: number;
  executionTimeMs?: number;
}

const RUN_BASE_URL: string =
  import.meta.env.VITE_TEST_FRESHERS_RUN_BASE_URL || "http://localhost:8092/api";

const baseQueryWithAuth = fetchBaseQuery({
  baseUrl: RUN_BASE_URL,
  prepareHeaders: (headers, { getState }) => {
    const state = getState() as RootState;
    const accessToken = state.authSlice?.accessToken ?? "";
    if (accessToken) {
      headers.set("Authorization", `Bearer ${accessToken}`);
    }
    return headers;
  },
});

export const InteractiveSqlApiSlice = createApi({
  reducerPath: "interactiveSqlApi",
  baseQuery: baseQueryWithAuth,
  tagTypes: ["SeedFiles"],
  endpoints: (builder) => ({
    getSeedFiles: builder.query<SeedFileDto[], void>({
      query: () => "/interactive-sql/seed/files",
      providesTags: ["SeedFiles"],
    }),
    getSeedFile: builder.query<SeedFileDto, string>({
      query: (tableName) => `/interactive-sql/seed/files/${tableName}`,
      providesTags: (_result, _error, tableName) => [{ type: "SeedFiles", id: tableName }],
    }),
    saveSeedFile: builder.mutation<InteractiveSqlExecuteResponse, CustomSeedRequest>({
      query: (payload) => ({
        url: "/interactive-sql/seed/save-file",
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["SeedFiles"],
    }),
    deleteSeedFile: builder.mutation<{ success: boolean }, string>({
      query: (tableName) => ({
        url: `/interactive-sql/seed/files/${tableName}`,
        method: "DELETE",
      }),
      invalidatesTags: ["SeedFiles"],
    }),
    executeInteractiveSql: builder.mutation<InteractiveSqlExecuteResponse, { sql: string }>({
      query: (payload) => ({
        url: "/interactive-sql/execute",
        method: "POST",
        body: payload,
      }),
    }),
  }),
});

export const {
  useGetSeedFilesQuery,
  useGetSeedFileQuery,
  useSaveSeedFileMutation,
  useDeleteSeedFileMutation,
  useExecuteInteractiveSqlMutation,
} = InteractiveSqlApiSlice;
